"use client"

import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, CheckCircle2 } from "lucide-react"

import DriverScreen from "../components/DriverScreen.jsx"
import StatusPill from "../components/StatusPill.jsx"
import styles from "../css/driver.module.css"
import api from "../../../api"
import LiveMap from "../../../components/LiveMap"

export default function CompletedDelivery() {
  const { jobId } = useParams()
  const navigate = useNavigate()

  const [job, setJob] = useState(null)
  const [routeData, setRouteData] = useState(null)
  const [trackingData, setTrackingData] = useState(null)
  const [proof, setProof] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!jobId) return

    async function load() {
      try {
        setLoading(true)
        setError(null)

        // Tracking carries the path the driver actually drove. Fetch it first:
        // when it's there we can render the whole view from it and skip the
        // billed route calculation entirely.
        const [detailsRes, trackingRes, proofRes] = await Promise.all([
          api.get(`/api/driver/deliveries/${jobId}`),
          api
            .get(`/api/driver/deliveries/${jobId}/tracking`)
            .catch(() => null), // best-effort; we fall back to the planned route
          api.get(`/api/driver/deliveries/${jobId}/proof`).catch(() => null),
        ])

        setJob(detailsRes?.data?.job || null)
        setProof(proofRes?.data?.data || null)

        const tracking = trackingRes?.data || null
        setTrackingData(tracking)

        const hasTracedPath = Array.isArray(tracking?.data?.completedPathLineString)
        if (hasTracedPath) {
          setRouteData(null)
          return
        }

        // No traced path (e.g. tracking never recorded) — fall back to the
        // planned route so the map still shows something.
        const routeRes = await api.get(`/api/driver/deliveries/${jobId}/route`, {
          params: { mode: "fastest" },
        })
        setRouteData(routeRes?.data || null)
      } catch (err) {
        console.error("[CompletedDelivery] load:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [jobId])

  const rawPaymentType = String(job?.paymentType || job?.paymenttype || "").trim()
  const isCash = /cash/i.test(rawPaymentType)
  const isCard = /card/i.test(rawPaymentType)
  const paymentType = isCash ? "Cash" : isCard ? "Card" : rawPaymentType || "Card"

  const amountRaw = job?.amount ?? job?.gross ?? job?.total ?? 0
  const total = (Number(amountRaw) || 0).toFixed(2)

  const cashReceived = job?.cashreceived ?? job?.cashReceived ?? job?.cash_received
  const cashChange = job?.cashchange ?? job?.cashChange ?? job?.cash_change

  const completedLineString = trackingData?.data?.completedPathLineString
  const plannedLineString = routeData?.route?.lineString
  const lineString = Array.isArray(completedLineString) ? completedLineString : plannedLineString

  const trackingDestination = trackingData?.data?.destination
  const routeDestination = routeData?.destination
  const destination = trackingDestination
    ? [trackingDestination.lng, trackingDestination.lat]
    : routeDestination
    ? [routeDestination.lng, routeDestination.lat]
    : null
  const mapCenter = destination || (lineString?.[0] ? lineString[0] : null)

  const money = (value) => (value != null ? `R${Number(value).toFixed(2)}` : "—")

  return (
    <DriverScreen>
      <h1 className={styles.screenTitle}>Delivery summary</h1>

      {loading && <div className={styles.statusMessage}>Loading delivery…</div>}

      {!loading && error && <div className={styles.errorMessage}>{error}</div>}

      {!loading && !error && !job && (
        <div className={styles.statusMessage}>Delivery not found.</div>
      )}

      {!loading && !error && job && (
        <>
          <div className={styles.panel}>
            <div className={styles.completedHeader}>
              <div>
                <div className={styles.stopPatient}>{job.patient || "Unnamed patient"}</div>
                <div className={styles.stopRef}>{job.scriptNumber || job.paymentid}</div>
              </div>
              <StatusPill status="done" />
            </div>

            <LiveMap
              center={mapCenter}
              destination={destination}
              routeLineString={Array.isArray(lineString) ? lineString : null}
              height={260}
              zoom={13}
            />

            {!completedLineString && (
              <div className={styles.statusMessage}>
                No tracked path was recorded for this delivery — showing the planned route.
              </div>
            )}
          </div>

          <div className={styles.panel}>
            <h2 className={styles.sectionLabel}>Payment</h2>

            <div className={styles.summaryGrid}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Total</span>
                <span className={styles.summaryValue}>R{total}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Method</span>
                <span className={styles.summaryValue}>{paymentType}</span>
              </div>

              {isCash && (
                <>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Cash received</span>
                    <span className={styles.summaryValue}>{money(cashReceived)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Change given</span>
                    <span className={styles.summaryValue}>{money(cashChange)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {proof?.signatureUrl && (
            <div className={styles.panel}>
              <h2 className={styles.sectionLabel}>Proof of delivery</h2>
              <img
                className={styles.signatureImage}
                src={proof.signatureUrl}
                alt="Recipient signature"
              />
              {proof.receivedBy && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryKey}>Received by</span>
                  <span className={styles.summaryValue}>{proof.receivedBy}</span>
                </div>
              )}
            </div>
          )}

          <div className={styles.statusMessage}>
            <CheckCircle2 size={16} aria-hidden="true" /> Delivery recorded as complete.
          </div>
        </>
      )}

      <button type="button" className={styles.secondaryBtn} onClick={() => navigate("/driver")}>
        <ArrowLeft size={18} aria-hidden="true" />
        Back to route
      </button>
    </DriverScreen>
  )
}
